// src/components/transfers/EditDialog.jsx
import React, { useState } from 'react';
import { X, Package } from 'lucide-react';
import ProductPicker from './ProductPicker';

const EditDialog = ({ transferNo, initialItems, products, onClose, onEdit }) => {
  const [items, setItems] = useState(initialItems.map(t => ({
    id: t.id, product_id: t.product_id, product_name: t.product_name,
    transfer_unit: t.transfer_unit || 'unit', box_quantity: t.box_quantity,
    box_count: t.box_count, quantity: t.quantity, deleted: false,
  })));
  const [addItems, setAddItems] = useState([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');

  const updateItem = (idx, field, value) => {
    setItems(prev => {
      const list = [...prev];
      list[idx] = { ...list[idx], [field]: value };
      if (field === 'box_count' && list[idx].box_quantity) {
        list[idx].quantity = parseInt(value || 1) * list[idx].box_quantity;
      }
      if (field === 'quantity' && list[idx].transfer_unit === 'unit') {
        list[idx].quantity = parseInt(value || 1);
      }
      return list;
    });
  };

  const toggleDelete = (idx) => {
    setItems(prev => { const list = [...prev]; list[idx] = { ...list[idx], deleted: !list[idx].deleted }; return list; });
  };

  const handleAddItem = (item) => setAddItems(prev => [...prev, item]);
  const removeAddItem = (idx) => setAddItems(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setEditing(true); setError('');
    try {
      const payload = [
        ...items.map(item => ({
          id: item.id, product_id: item.product_id,
          action: item.deleted ? 'remove' : 'update',
          quantity: item.transfer_unit === 'box' ? parseInt(item.box_count || 1) : parseInt(item.quantity || 1),
          transfer_unit: item.transfer_unit,
        })),
        ...addItems.map(item => ({
          action: 'add', product_id: parseInt(item.product_id),
          quantity: item.transfer_unit === 'box' ? parseInt(item.box_count || 1) : parseInt(item.quantity || 1),
          transfer_unit: item.transfer_unit || 'unit',
        })),
      ];
      await onEdit(transferNo, { items: payload });
    } catch (err) {
      setError(err.message || '编辑失败');
    } finally {
      setEditing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold">编辑调拨单</h3>
            <p className="text-xs text-gray-500 mt-0.5">{transferNo}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          {/* 现有明细 */}
          <div>
            <label className="block text-sm font-medium mb-2">现有商品</label>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={item.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${item.deleted ? 'bg-red-50 border-red-200 opacity-60' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${item.deleted ? 'line-through text-gray-400' : ''}`}>{item.product_name}</p>
                    {!item.deleted && (
                      item.transfer_unit === 'box' ? (
                        <div className="flex items-center gap-2 mt-1">
                          <button onClick={() => updateItem(idx, 'box_count', Math.max(1, (item.box_count || 1) - 1))} className="w-6 h-6 rounded bg-white border text-xs flex items-center justify-center hover:bg-gray-100">−</button>
                          <span className="text-sm font-bold text-orange-600">{item.box_count || 1} 箱</span>
                          <button onClick={() => updateItem(idx, 'box_count', (item.box_count || 1) + 1)} className="w-6 h-6 rounded bg-white border text-xs flex items-center justify-center hover:bg-gray-100">＋</button>
                          <span className="text-xs text-gray-400">= {item.quantity} 个</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <button onClick={() => updateItem(idx, 'quantity', Math.max(1, (item.quantity || 1) - 1))} className="w-6 h-6 rounded bg-white border text-xs flex items-center justify-center hover:bg-gray-100">−</button>
                          <span className="text-sm font-bold text-gray-700">{item.quantity} 个</span>
                          <button onClick={() => updateItem(idx, 'quantity', (item.quantity || 1) + 1)} className="w-6 h-6 rounded bg-white border text-xs flex items-center justify-center hover:bg-gray-100">＋</button>
                        </div>
                      )
                    )}
                    {item.deleted && <p className="text-xs text-red-500 mt-0.5">将被删除</p>}
                  </div>
                  <button onClick={() => toggleDelete(idx)}
                    className={`flex-shrink-0 px-2 py-1 rounded-lg text-xs font-medium border transition-colors ${item.deleted ? 'bg-gray-100 text-gray-600 border-gray-300' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'}`}>
                    {item.deleted ? '恢复' : '删除'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* 新增商品 */}
          <div>
            <label className="block text-sm font-medium mb-2">新增商品</label>
            <ProductPicker products={products} selectedItems={addItems} onAdd={handleAddItem} />
            {addItems.length > 0 && (
              <div className="space-y-2 mt-2">
                {addItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-green-50 p-3 rounded-xl border border-green-200">
                    <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                      {item.product_image ? <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center"><Package className="w-4 h-4 text-gray-400" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate text-green-800">{item.product_name}</p>
                      <p className="text-xs text-green-600">{item.transfer_unit === 'box' ? `${item.box_count} 箱 = ${item.quantity} 个` : `${item.quantity} 个`}</p>
                    </div>
                    <button onClick={() => removeAddItem(idx)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>}
        </div>
        <div className="p-6 border-t flex space-x-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">取消</button>
          <button onClick={handleSubmit} disabled={editing}
            className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg ${editing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}>
            {editing ? '保存中...' : '保存修改'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDialog;
